import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DOCUMENT } from '../constants/documentTheme';
import MemoriesInviteEmails from './MemoriesInviteEmails';

const listScrollSx = {
  maxHeight: 420,
  overflowY: 'auto',
  border: `1px solid ${DOCUMENT.rule}`,
  backgroundColor: '#fff',
  px: 1.5,
  py: 0.5,
};

const rowSx = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 1,
  py: 1.25,
  borderBottom: `1px solid ${DOCUMENT.rule}`,
  '&:last-child': { borderBottom: 0 },
};

export function isGuestListMember(member) {
  if (!member) return false;
  if (member.role === 'guest_contributor') return true;
  if (member.role === 'owner') return false;
  return member.source === 'qr';
}

const sourceLabelKey = (source, role) => {
  if (role === 'owner') return 'memories.memberSourceOwner';
  if (role === 'guest_contributor') return 'memories.memberSourceGuest';
  if (source === 'trip_sync') return 'memories.memberSourceTrip';
  if (source === 'invite') return 'memories.memberSourceInvite';
  if (source === 'qr') return 'memories.memberSourceGuest';
  return 'memories.memberSourceManual';
};

const matchesQuery = (haystack, query) => {
  if (!query) return true;
  return String(haystack || '').toLowerCase().includes(query);
};

export function filterPeopleLists(members, pendingInvites, declinedInvites, search) {
  const query = String(search || '').trim().toLowerCase();
  const filteredPending = (pendingInvites || []).filter((invite) => (
    matchesQuery(invite.email, query)
  ));
  const filteredDeclined = (declinedInvites || []).filter((invite) => (
    matchesQuery(invite.email, query)
  ));
  const filteredMembers = (members || []).filter((member) => (
    matchesQuery(member.display_name, query) || matchesQuery(member.email, query)
  ));
  const filteredGuests = filteredMembers.filter((member) => isGuestListMember(member));
  const filteredRoster = filteredMembers.filter((member) => !isGuestListMember(member));
  return {
    query,
    filteredPending,
    filteredDeclined,
    filteredMembers,
    filteredGuests,
    filteredRoster,
  };
}

const MemberRow = ({
  member,
  t,
  isOwner,
  primaryOwnerId,
  currentUserId,
  canAssignOwners,
  onUpdateMemberRole,
  onRemoveMember,
  busyId,
  confirmRemoveId,
  setConfirmRemoveId,
  emphasizeName = false,
}) => {
  const isPrimaryOwner = primaryOwnerId != null
    ? member.user_id != null && String(member.user_id) === String(primaryOwnerId)
    : member.role === 'owner';
  const canRemove = isOwner && !isPrimaryOwner;
  const showRoleAction = Boolean(
    canAssignOwners
    && onUpdateMemberRole
    && member.user_id
    && member.role !== 'guest_contributor'
    && !isPrimaryOwner
    && String(member.user_id) !== String(currentUserId)
  );
  const confirming = confirmRemoveId === member.id;
  const photoCount = Number(member.photo_count || 0);

  return (
    <Box key={member.id} sx={rowSx} data-testid={`memories-person-${member.id}`}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            color: DOCUMENT.ink,
            fontSize: emphasizeName ? 16 : 14,
            fontFamily: emphasizeName ? DOCUMENT.display : DOCUMENT.sans,
            fontWeight: emphasizeName ? 500 : 400,
            lineHeight: 1.3,
          }}
        >
          {member.display_name || member.email || t('memories.unnamedGuest')}
        </Typography>
        {member.email ? (
          <Typography sx={{ color: DOCUMENT.ink3, fontSize: 12, overflowWrap: 'anywhere', mt: 0.25 }}>
            {member.email}
          </Typography>
        ) : null}
        <Typography sx={{ color: DOCUMENT.ink3, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', mt: 0.35 }}>
          {t(sourceLabelKey(member.source, member.role))}
          {member.role !== 'owner' && member.role !== 'guest_contributor'
            ? ` · ${t('memories.inviteStatusAccepted')}`
            : ''}
          {photoCount > 0
            ? ` · ${t('memories.memberPhotoCount', { count: photoCount })}`
            : ''}
        </Typography>
        {confirming && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              disabled={busyId === `member-${member.id}`}
              onClick={() => {
                onRemoveMember(member);
                setConfirmRemoveId(null);
              }}
              sx={{
                backgroundColor: DOCUMENT.oxblood,
                color: DOCUMENT.ivory,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: 10,
                borderRadius: '2px',
                '&:hover': { backgroundColor: '#5C1A22' },
              }}
            >
              {t('memories.removeAccessConfirm')}
            </Button>
            <Button
              size="small"
              onClick={() => setConfirmRemoveId(null)}
              sx={{ color: DOCUMENT.ink3, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 10 }}
            >
              {t('common.cancel')}
            </Button>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25, flexShrink: 0 }}>
        {showRoleAction && !confirming && (
          <Button
            size="small"
            disabled={busyId === `member-role-${member.id}`}
            onClick={() => onUpdateMemberRole(member, member.role === 'owner' ? 'contributor' : 'owner')}
            data-testid={`memories-member-role-${member.id}`}
            sx={{
              color: DOCUMENT.oxblood,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: 10,
              whiteSpace: 'nowrap',
            }}
          >
            {member.role === 'owner' ? t('memories.removeOwner') : t('memories.makeOwner')}
          </Button>
        )}
        {canRemove && !confirming && (
          <Button
            size="small"
            disabled={busyId === `member-${member.id}`}
            onClick={() => setConfirmRemoveId(member.id)}
            sx={{
              color: DOCUMENT.ink3,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: 10,
              whiteSpace: 'nowrap',
            }}
          >
            {t('memories.removeAccess')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

const MemoriesPeoplePanel = ({
  isOwner,
  members,
  pendingInvites,
  declinedInvites = [],
  inviteEmails,
  onInviteEmailsChange,
  inviteDraft,
  onInviteDraftChange,
  inviting,
  inviteMessage,
  onSendInvites,
  busyId,
  onRemoveMember,
  onRevokeInvite,
  onResendInvite,
  canAssignOwners = false,
  primaryOwnerId = null,
  currentUserId = null,
  onUpdateMemberRole,
}) => {
  const { t } = useTranslation();
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [search, setSearch] = useState('');

  const {
    query,
    filteredPending,
    filteredDeclined,
    filteredGuests,
    filteredRoster,
  } = useMemo(
    () => filterPeopleLists(members, pendingInvites, declinedInvites, search),
    [members, pendingInvites, declinedInvites, search]
  );

  const guestTotal = (members || []).filter((member) => isGuestListMember(member)).length;
  const rosterTotal = (members || []).length - guestTotal;
  const showSearch = members.length + pendingInvites.length + declinedInvites.length > 0;

  const rowProps = {
    t,
    isOwner,
    primaryOwnerId,
    currentUserId,
    canAssignOwners,
    onUpdateMemberRole,
    onRemoveMember,
    busyId,
    confirmRemoveId,
    setConfirmRemoveId,
  };

  return (
    <Box>
      {isOwner && (
        <Box sx={{ border: `1px solid ${DOCUMENT.rule}`, backgroundColor: DOCUMENT.surface, p: 2.5, mb: 3 }}>
          <MemoriesInviteEmails
            emails={inviteEmails}
            onChange={onInviteEmailsChange}
            draft={inviteDraft}
            onDraftChange={onInviteDraftChange}
            showSubmit
            submitting={inviting}
            onSubmit={onSendInvites}
            fieldSx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '2px',
                backgroundColor: '#fff',
              },
            }}
          />
          {inviteMessage && (
            <Typography sx={{ color: DOCUMENT.ink2, fontSize: 13, mt: 1 }}>{inviteMessage}</Typography>
          )}
        </Box>
      )}

      {showSearch && (
        <TextField
          fullWidth
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('memories.peopleSearchPlaceholder')}
          inputProps={{ 'aria-label': t('memories.peopleSearchPlaceholder'), 'data-testid': 'memories-people-search' }}
          sx={{
            mb: 2.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: '2px',
              backgroundColor: '#fff',
            },
          }}
        />
      )}

      {isOwner && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3, mb: 1 }}>
            {t('memories.pendingInvites')}
            {' · '}
            {query ? `${filteredPending.length}/${pendingInvites.length}` : pendingInvites.length}
          </Typography>
          {pendingInvites.length === 0 ? (
            <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13 }}>{t('memories.pendingInvitesEmpty')}</Typography>
          ) : filteredPending.length === 0 ? (
            <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13 }}>{t('memories.peopleSearchEmpty')}</Typography>
          ) : (
            <Box sx={listScrollSx} data-testid="memories-pending-list">
              {filteredPending.map((invite) => (
                <Box key={invite.id} sx={rowSx}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: DOCUMENT.ink, fontSize: 14, overflowWrap: 'anywhere' }}>
                      {invite.email}
                    </Typography>
                    <Typography sx={{ color: DOCUMENT.accent, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', mt: 0.25 }}>
                      {t('memories.inviteStatusPending')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Button
                      size="small"
                      disabled={busyId === `invite-resend-${invite.id}` || busyId === `invite-${invite.id}`}
                      onClick={() => onResendInvite?.(invite)}
                      sx={{
                        color: DOCUMENT.oxblood,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        fontSize: 10,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t('memories.inviteResend')}
                    </Button>
                    <IconButton
                      size="small"
                      aria-label={t('memories.revokeInvite')}
                      disabled={busyId === `invite-${invite.id}` || busyId === `invite-resend-${invite.id}`}
                      onClick={() => onRevokeInvite(invite)}
                      sx={{ color: DOCUMENT.ink3 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {isOwner && declinedInvites.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3, mb: 1 }}>
            {t('memories.declinedInvites')}
            {' · '}
            {query ? `${filteredDeclined.length}/${declinedInvites.length}` : declinedInvites.length}
          </Typography>
          {filteredDeclined.length === 0 ? (
            <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13 }}>{t('memories.peopleSearchEmpty')}</Typography>
          ) : (
            <Box sx={listScrollSx} data-testid="memories-declined-list">
              {filteredDeclined.map((invite) => (
                <Box key={invite.id} sx={rowSx}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: DOCUMENT.ink, fontSize: 14, overflowWrap: 'anywhere' }}>
                      {invite.email}
                    </Typography>
                    <Typography sx={{ color: '#9C3B2A', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', mt: 0.25 }}>
                      {t('memories.inviteStatusDeclined')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Button
                      size="small"
                      disabled={busyId === `invite-resend-${invite.id}` || busyId === `invite-${invite.id}`}
                      onClick={() => onResendInvite?.(invite)}
                      sx={{
                        color: DOCUMENT.oxblood,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        fontSize: 10,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t('memories.inviteResend')}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ mb: guestTotal > 0 || filteredGuests.length > 0 ? 3 : 0 }}>
        <Typography sx={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3, mb: 1 }}>
          {t('memories.membersHeading')}
          {' · '}
          {query ? `${filteredRoster.length}/${rosterTotal}` : rosterTotal}
        </Typography>
        {rosterTotal === 0 ? (
          <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13 }}>{t('memories.membersEmpty')}</Typography>
        ) : filteredRoster.length === 0 ? (
          <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13 }}>{t('memories.peopleSearchEmpty')}</Typography>
        ) : (
          <Box sx={listScrollSx} data-testid="memories-members-list">
            {filteredRoster.map((member) => (
              <MemberRow key={member.id} member={member} {...rowProps} />
            ))}
          </Box>
        )}
      </Box>

      {(guestTotal > 0 || filteredGuests.length > 0) && (
        <Box>
          <Typography sx={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3, mb: 1 }}>
            {t('memories.guestsHeading')}
            {' · '}
            {query ? `${filteredGuests.length}/${guestTotal}` : guestTotal}
          </Typography>
          {filteredGuests.length === 0 ? (
            <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13 }}>{t('memories.peopleSearchEmpty')}</Typography>
          ) : (
            <Box sx={listScrollSx} data-testid="memories-guests-list">
              {filteredGuests.map((member) => (
                <MemberRow key={member.id} member={member} emphasizeName {...rowProps} />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MemoriesPeoplePanel;
